import { useMemo, useState } from 'react';
import { Icon } from '../../lib/icons';

/**
 * A video or a recording texted in (1.12.32, Teampace 2961; Joey: "I sent a video from my mobile
 * phone to stitch. I would love to be able to have those show up just like any other media file").
 * It plays in the conversation; one this browser can't play (3GP in Chrome, AMR voice notes) offers
 * the download instead, as does any that fails to load.
 */
export function TextMedia({ url, contentType, from }: { url: string; contentType: string; from: string }) {
  const video = /^video\//.test(contentType);
  const [broken, setBroken] = useState(false);
  const playable = useMemo(() => {
    try {
      const el = document.createElement(video ? 'video' : 'audio');
      // MOV from iPhones is H.264 in a QuickTime box: browsers that say no to the type often play it as MP4.
      return el.canPlayType(contentType) !== '' || (contentType === 'video/quicktime' && el.canPlayType('video/mp4') !== '');
    } catch {
      return true;
    }
  }, [video, contentType]);
  const what = video ? 'video' : 'recording';
  if (!playable || broken) {
    return (
      <a className="msg-file" href={url} download>
        <Icon name={video ? 'video' : 'volume'} size={15} /> {`This ${what} can’t play here. Download it`}
      </a>
    );
  }
  return video ? (
    <figure className="msg-text-video">
      <video src={url} controls playsInline preload="metadata" aria-label={`Video from ${from}`} onError={() => setBroken(true)} />
      <a className="msg-text-media-dl" href={url} download aria-label={`Download the video from ${from}`} title="Download">
        <Icon name="download" size={14} />
      </a>
    </figure>
  ) : (
    <figure className="msg-text-audio">
      <Icon name="volume" size={16} />
      <audio src={url} controls preload="metadata" aria-label={`Recording from ${from}`} onError={() => setBroken(true)} />
      <a className="msg-text-media-dl" href={url} download aria-label={`Download the recording from ${from}`} title="Download">
        <Icon name="download" size={14} />
      </a>
    </figure>
  );
}
